import { Box } from '@material-ui/core';
import {
  BorrowerOnboardingDetails,
  DocumentStatus,
  OnboardingDetailsRole,
  postSendQrCardExpedition,
} from '@nemuru-eng/js-api-client';
import { useEffect, useMemo } from 'react';

import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useRequest } from '@/hooks/requests/useRequest';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

const CONFIRMATION_NEXT_STEPS = (borrowerOnboardingDetails: BorrowerOnboardingDetails) => [
  <Text tId="CONFIRMATION_NEXT_STEP_1" dynamicDataTexts={[borrowerOnboardingDetails.invitation.email!]} />,
  <Text tId="CONFIRMATION_NEXT_STEP_2" />,
  <Text tId="CONFIRMATION_NEXT_STEP_3" />,
];

export const Confirmation = () => {
  const { loan } = useDocumentsValidationContext();
  const { borrowerDocumentationRequired } = useDocumentsValidationContext();
  const { sm } = useScreen();
  const { request, loading } = useRequest();
  const { palette } = useCustomTheme();
  const { opened, setOpened } = useDialog();

  useEffect(() => {
    if (
      borrowerDocumentationRequired.requiredFiles &&
      borrowerDocumentationRequired.requiredFiles.every((file) => file.status === DocumentStatus.VALIDATED)
    ) {
      request(postSendQrCardExpedition, { loanId: loan.id });
    }
  }, [borrowerDocumentationRequired]);

  const documentationInIncidence = useMemo(() => {
    return (
      borrowerDocumentationRequired.requiredFiles &&
      borrowerDocumentationRequired.requiredFiles.filter((file) => file.status === DocumentStatus.INCIDENCE)
    );
  }, [borrowerDocumentationRequired]);

  const allDocumentationValidated = documentationInIncidence?.length === 0;

  const handleRetry = async () => {
    try {
      request(postSendQrCardExpedition, { loanId: loan.id });
    } catch (error) {
      console.error(error);
    }
  };

  const borrowerOnboardingDetails = loan.onboardingDetails.find((e) => e.role === OnboardingDetailsRole.BORROWER);

  if (!borrowerOnboardingDetails) {
    throw new Error('Borrower onboarding details not found');
  }

  return (
    <Box>
      <Box p={sm ? 2 : 0}>
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Icon
            size={sm ? 64 : 48}
            color={allDocumentationValidated ? palette.success.main : palette.error.main}
            style={{ marginBottom: '12px' }}
          >
            {allDocumentationValidated ? 'check_circle' : 'error_outline'}
          </Icon>
          <Box mb={2}>
            <Font variant="h3" color="title">
              <Text
                strong
                tId={
                  allDocumentationValidated
                    ? 'DOCUMENTS_VALIDATION_CONFIRMATION'
                    : 'DOCUMENTS_VALIDATION_CONFIRMATION_ERROR_TITLE'
                }
              />
            </Font>
          </Box>
          <Box mb={2}>
            <Font variant="subtitle2" color="textSecondary">
              <Text
                tId={
                  allDocumentationValidated
                    ? 'DOCUMENTS_VALIDATION_CONFIRMATION_SUCCESS'
                    : 'DOCUMENTS_VALIDATION_CONFIRMATION_ERROR'
                }
              />
              <Box mt={2} />
              {allDocumentationValidated ? (
                <>
                  {allDocumentationValidated ? (
                    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} gridGap={4}>
                      <Text tId={'NEXT_STEPS'} />:
                      <ol>
                        {CONFIRMATION_NEXT_STEPS(borrowerOnboardingDetails as BorrowerOnboardingDetails).map(
                          (step, index) => (
                            <li key={index}>{step}</li>
                          ),
                        )}
                      </ol>
                    </Box>
                  ) : (
                    <Text tId={'DOCUMENTS_VALIDATION_CONFIRMATION_ERROR'} />
                  )}
                  <Font variant="caption" color="textSecondary">
                    <div style={{ paddingTop: '32px' }}>
                      <Text tId={'SEND_EMAIL_AGAIN_DESCRIPTION_PART_1'} />
                      <span
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: palette.info.dark,
                        }}
                        onClick={() => {
                          setOpened(true);
                        }}
                      >
                        <Text tId={'HERE'} />
                      </span>
                      <Text tId={'SEND_EMAIL_AGAIN_DESCRIPTION_PART_2'} />
                    </div>
                  </Font>
                </>
              ) : (
                <Box textAlign={'left'}>
                  {documentationInIncidence?.map((file) => (
                    <Box
                      ml={1}
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'flex-start'}
                      gridGap={4}
                    >
                      <Icon size={16} color={palette.error.main}>
                        close
                      </Icon>
                      <Text strong tId={file.name} />
                    </Box>
                  ))}
                </Box>
              )}
            </Font>
          </Box>
        </Box>
      </Box>
      <ActionDialog
        opened={opened}
        setOpened={setOpened}
        handleClose={() => setOpened(false)}
        Title={<Text tId={'SEND_EMAIL_AGAIN'} />}
        Description={
          <Text tId={'SEND_EMAIL_AGAIN_DIALOG'} dynamicDataTexts={[borrowerOnboardingDetails.invitation.email!]} />
        }
        MainButton={<Text tId={'SEND'} />}
        onSubmit={async () => {
          await handleRetry();
          setOpened(false);
        }}
        loading={loading}
      />
    </Box>
  );
};
